import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  RotateCcw,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { TruncatedText } from "@/components/ui/truncated-text";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { ProhibitedKeywordsOverviewModal } from "./ProhibitedKeywordsOverviewModal";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { DeleteHeroAsinDialog, DeleteHeroAsinTarget } from "@/components/shared/DeleteHeroAsinDialog";
import { resolveProductImage } from "@/assets/products";
import { ProductListItem } from "@/types";
import { SearchTermSetCard } from "./SearchTermSetCard";
import { GenerateSearchTermsModal } from "./GenerateSearchTermsModal";
import { AMAZON_ASIN_URL } from "@/constants/amazon";
import { useGetSearchTermSets, useRetrySearchTermSet } from "@/resources/search-term/search-term.api";
import type { SearchTermSet } from "@/types/search-term";
import { SearchTermStatus } from "@/types/search-term";
import { STALE_THRESHOLD_MS } from "./staleThreshold";

const isStaleSet = (set: SearchTermSet) =>
  Date.now() - new Date(set.created_at).getTime() > STALE_THRESHOLD_MS;

interface SearchTermsProductCardProps {
  product: ProductListItem;
  brandId: number;
  defaultOpen?: boolean;
}

export function SearchTermsProductCard({
  product,
  brandId,
  defaultOpen = false,
}: SearchTermsProductCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [prohibitedOpen, setProhibitedOpen] = useState(false);
  const [showOlderVersions, setShowOlderVersions] = useState(false);
  const [asinToDelete, setAsinToDelete] = useState<DeleteHeroAsinTarget | null>(null);
  const viewerReadOnly = useViewerReadOnly();

  const { data: searchTermSets = [], isLoading } = useGetSearchTermSets(product.id);

  const completedSets = searchTermSets.filter(
    (s) => s.status === SearchTermStatus.Completed || s.status === SearchTermStatus.Refreshing,
  );
  const pendingSets = searchTermSets.filter(
    (s) => s.status === SearchTermStatus.Pending || s.status === SearchTermStatus.Generating,
  );
  const failedSets = searchTermSets.filter((s) => s.status === SearchTermStatus.Failed);

  const retryMutation = useRetrySearchTermSet();
  const handleRetry = (set: SearchTermSet) => {
    retryMutation.mutate({ id: set.id, productId: product.id, type: set.type });
  };

  const latestSet = completedSets.length > 0 ? completedSets[0] : null;
  const olderSets = completedSets.slice(1);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="rounded-lg border border-border bg-card">
        <CollapsibleTrigger asChild>
          <div className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors text-left cursor-pointer">
            <div className="flex items-center gap-4">
              {isOpen ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
              )}
              {/* AUT-279 (reopened): the client rejected the grey bars, so the box is bg-white. */}
              <div className="h-14 w-14 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
                <img
                  src={resolveProductImage(product.image_url ?? "", product.asin)}
                  alt={product.name}
                  className="h-full w-full object-contain p-1"
                />
              </div>
              <div>
                <div className="font-semibold text-foreground">
                  <TruncatedText lines={1}>{product.name}</TruncatedText>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <a
                    href={`${AMAZON_ASIN_URL}/${product.asin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {product.asin}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  {completedSets.length > 1 && (
                    <span className="text-sm text-muted-foreground">
                      · {completedSets.length} version{completedSets.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {!viewerReadOnly && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground"
                  onClick={() => setProhibitedOpen(true)}
                >
                  <ShieldAlert className="h-4 w-4 mr-1" />
                  Prohibited Keywords
                </Button>
              )}
              <Button
                size="sm"
                variant="outline-on-white"
                onClick={() => setGenerateModalOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Generate
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => setAsinToDelete({ id: product.id, name: product.name })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {pendingSets.map((set) => (
                  <div
                    key={set.id}
                    className="rounded-lg border border-border p-4 flex flex-col items-center justify-center gap-2 text-muted-foreground"
                  >
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Generating search terms...</span>
                    </div>
                    {isStaleSet(set) && (
                      <div className="flex items-center gap-1 text-amber-600 dark:text-amber-500 text-xs">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <span>This is taking longer than expected. You may want to try again.</span>
                      </div>
                    )}
                  </div>
                ))}

                {failedSets.map((set) => (
                  <div
                    key={set.id}
                    className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2 text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">Search term generation failed.</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={retryMutation.isPending}
                      onClick={() => handleRetry(set)}
                    >
                      {retryMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RotateCcw className="h-4 w-4" />
                      )}
                      Retry
                    </Button>
                  </div>
                ))}

                {completedSets.length === 0 && pendingSets.length === 0 && failedSets.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No search term sets generated yet.</p>
                    <Button
                      variant="outline-on-white"
                      className="mt-3"
                      onClick={() => setGenerateModalOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Generate Search Terms
                    </Button>
                  </div>
                ) : (
                  <>
                    {latestSet && (
                      <SearchTermSetCard
                        key={latestSet.id}
                        termSet={latestSet}
                        productId={product.id}
                        productName={product.name}
                        brandId={product.brand_id ?? brandId}
                        isLatest
                      />
                    )}

                    {olderSets.length > 0 && (
                      <>
                        {!showOlderVersions ? (
                          <Button
                            variant="ghost"
                            className="w-full text-muted-foreground"
                            onClick={() => setShowOlderVersions(true)}
                          >
                            View {olderSets.length} older version{olderSets.length !== 1 ? "s" : ""}
                          </Button>
                        ) : (
                          <>
                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pt-2">
                              Previous Versions
                            </div>
                            {olderSets.map((termSet) => (
                              <SearchTermSetCard
                                key={termSet.id}
                                termSet={termSet}
                                productId={product.id}
                                productName={product.name}
                                brandId={product.brand_id ?? brandId}
                              />
                            ))}
                            <Button
                              variant="ghost"
                              className="w-full text-muted-foreground"
                              onClick={() => setShowOlderVersions(false)}
                            >
                              Hide older versions
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </CollapsibleContent>
      </div>

      <GenerateSearchTermsModal
        open={generateModalOpen}
        onOpenChange={setGenerateModalOpen}
        productId={product.id}
        productName={product.name}
      />

      {!viewerReadOnly && (
        <ProhibitedKeywordsOverviewModal
          open={prohibitedOpen}
          onOpenChange={setProhibitedOpen}
          brandId={product.brand_id ?? brandId}
          productId={product.id}
          productName={product.name}
        />
      )}

      <DeleteHeroAsinDialog
        asin={asinToDelete}
        brandId={brandId}
        onCancel={() => setAsinToDelete(null)}
        onDeleted={() => setAsinToDelete(null)}
      />
    </Collapsible>
  );
}
